import { t } from "./i18n";
import { Bell, Clock, RefreshCw, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { registerSW } from "virtual:pwa-register";
import { useBackHandler } from "./backNavigation";
import { restartIntoUpdate, waitForInstalledWorker, watchForUpdates } from "./pwaUpdate";
import { AppShell, type Screen } from "./components/AppShell";
import { LoginScreen } from "./components/LoginScreen";
import { TwoFactorScreen } from "./components/TwoFactorScreen";
import { Toast } from "./components/Toast";
import { HistoryScreen } from "./screens/HistoryScreen";
import { OrderScreen } from "./screens/OrderScreen";
import { ProductsScreen } from "./screens/ProductsScreen";
import { SettingsScreen } from "./screens/SettingsScreen";
import { TablesScreen } from "./screens/TablesScreen";
import {
  applyFontFamily,
  applyTextPrefs,
  applyThemeColor,
  loadFontFamily,
  loadKeepUnsentDraft,
  loadProductScrollMode,
  loadThemeColor,
  saveThemeColor,
  type ThemeColor,
  loadTextBold,
  loadTextSizePercent,
  loadTheme,
  saveTheme,
  type ThemePreference
} from "./storage";
import { useEasyOrders } from "./useEasyOrders";
import { useLanguage } from "./useLanguage";
import { diagnostics } from "./diagnostics";
import { isOrderRecorded, pendingForTable } from "./utils";

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

function isStandalone(): boolean {
  return window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

function isAppleMobile(): boolean {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export default function App() {
  useLanguage();
  const easyOrders = useEasyOrders();
  const [screen, setScreen] = useState<Screen>("tables");
  /* The open order being moved, while the tables grid acts as its destination picker. */
  const [changeTable, setChangeTable] = useState<{ orderId: number; sourceLabel: string } | null>(null);
  const [settingsHomeKey, setSettingsHomeKey] = useState(0);
  const [tableLabel, setTableLabel] = useState("");
  const [theme, setTheme] = useState<ThemePreference>(() => loadTheme());
  const [themeColor, setThemeColor] = useState<ThemeColor>(() => loadThemeColor());
  // Text prefs live on the document root, so re-apply them on every load.
  useEffect(() => {
    applyTextPrefs(loadTextSizePercent(), loadTextBold());
    applyFontFamily(loadFontFamily());
  }, []);
  useEffect(() => {
    applyThemeColor(themeColor);
    saveThemeColor(themeColor);
  }, [themeColor]);

  /* The Android status and navigation bars are painted from <meta name="theme-color">, which is
     otherwise a fixed red from index.html and looks wrong under every other palette. Read the
     resolved surface colour after the theme attributes are on the root and mirror it there. */
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      return;
    }
    const surface = getComputedStyle(document.documentElement).getPropertyValue("--surface").trim();
    if (surface) {
      meta.setAttribute("content", surface);
    }
  }, [theme, themeColor]);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updatePromptOpen, setUpdatePromptOpen] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [checkingUpdate, setCheckingUpdate] = useState(false);
  const serviceWorkerRegistration = useRef<ServiceWorkerRegistration | null>(null);
  /* Only the window whose user chose to restart reloads; other windows just get the prompt. */
  const restartRequested = useRef(false);
  /* The version the user put off with "Restart later": not offered again until a newer one arrives
     or they check for updates themselves. */
  const postponedUpdate = useRef<ServiceWorker | null>(null);
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null);
  const [notificationPrompt, setNotificationPrompt] = useState(false);

  /* A new version is ready. Restarting is the user's call — never under their hands mid-order —
     so ask, and keep the Settings badge as a reminder if they choose to finish their work first.
     Closing and reopening the app also switches to it, as browsers do for any waiting version. */
  const announceUpdate = useCallback(() => {
    setUpdateAvailable(true);
    const waiting = serviceWorkerRegistration.current?.waiting ?? null;
    if (!waiting || waiting !== postponedUpdate.current) {
      setUpdatePromptOpen(true);
    }
  }, []);

  const serviceWorkerStarted = useRef(false);
  useEffect(() => {
    if (serviceWorkerStarted.current) {
      return;
    }
    serviceWorkerStarted.current = true;
    registerSW({
      immediate: true,
      onNeedRefresh: announceUpdate,
      // vite-plugin-pwa reloads every open window once a new version takes over. Only the one that
      // asked for the restart should; the others must not lose what is on screen.
      onNeedReload: () => {
        if (restartRequested.current) {
          window.location.reload();
        }
      },
      onRegisteredSW: (_url, registration) => {
        if (registration) {
          serviceWorkerRegistration.current = registration;
          watchForUpdates(registration, announceUpdate);
          void registration.update();
          window.setInterval(() => void registration.update(), 60_000);
        }
      }
    });
  }, [announceUpdate]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) {
      return undefined;
    }
    let controlled = Boolean(navigator.serviceWorker.controller);
    // Another window restarted into a new version, so this one is now running the old code.
    const onControllerChange = () => {
      if (!controlled) {
        controlled = true; // the first install taking control is not an update
        return;
      }
      if (!restartRequested.current) {
        announceUpdate();
      }
    };
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);
    return () => navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
  }, [announceUpdate]);

  const restartNow = useCallback(() => {
    restartRequested.current = true;
    setRestarting(true);
    restartIntoUpdate(serviceWorkerRegistration.current);
  }, []);

  const restartLater = useCallback(() => {
    postponedUpdate.current = serviceWorkerRegistration.current?.waiting ?? null;
    setUpdatePromptOpen(false);
  }, []);

  const checkForPwaUpdate = useCallback(async () => {
    if (!("serviceWorker" in navigator)) {
      return;
    }
    setCheckingUpdate(true);
    try {
      const registration = serviceWorkerRegistration.current
        ?? await navigator.serviceWorker.getRegistration();
      if (registration) {
        serviceWorkerRegistration.current = registration;
        await registration.update();
        // update() resolves as soon as a new version starts downloading; wait until it is ready,
        // otherwise the check reports "up to date" while the download is still running.
        if (await waitForInstalledWorker(registration) && navigator.serviceWorker.controller) {
          postponedUpdate.current = null;
          announceUpdate();
        }
      }
    } catch {
      // A failed check is harmless; the minute interval and the next manual check retry it.
    } finally {
      setCheckingUpdate(false);
    }
  }, [announceUpdate]);

  useBackHandler(updatePromptOpen && !restarting, restartLater);

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => {
      const resolved = theme === "system" ? (media.matches ? "dark" : "light") : theme;
      document.documentElement.dataset.theme = resolved;
    };
    apply();
    media.addEventListener("change", apply);
    saveTheme(theme);
    return () => media.removeEventListener("change", apply);
  }, [theme]);

  useEffect(() => {
    const capture = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as InstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", capture);
    return () => window.removeEventListener("beforeinstallprompt", capture);
  }, []);

  useEffect(() => {
    if (!easyOrders.session) {
      setTableLabel("");
      setScreen("tables");
    }
  }, [easyOrders.session]);

  useEffect(() => {
    if (
      !easyOrders.session ||
      !("Notification" in window) ||
      Notification.permission !== "default"
    ) {
      return;
    }
    const key = "easyorders.pwa.notificationsAsked";
    if (!localStorage.getItem(key)) {
      setNotificationPrompt(true);
    }
  }, [easyOrders.session]);

  const openTable = useCallback((label: string) => {
    easyOrders.ensureDraft(label);
    const tableId = easyOrders.setup?.shop_tables.find((table) => table.label === label)?.id;
    diagnostics.record({
      name: "table.view",
      category: "USER_ACTION",
      outcome: "succeeded",
      actionId: diagnostics.newActionId(),
      entityType: tableId ? "table" : undefined,
      entityId: tableId,
      metadata: { operation: "open", table_id: tableId }
    });
    setTableLabel(label);
  }, [easyOrders]);

  /* Every way out of a table funnels through here so the "keep unsent products" preference is
     honoured whether the waiter taps back or jumps straight to another tab. */
  const leaveTable = useCallback((actionId = diagnostics.newActionId()) => {
    const tableId = easyOrders.setup?.shop_tables.find((table) => table.label === tableLabel)?.id;
    if (tableLabel && !loadKeepUnsentDraft()) {
      easyOrders.discardDraft(tableLabel, actionId);
    }
    if (tableLabel) {
      diagnostics.record({
        name: "table.view",
        category: "USER_ACTION",
        outcome: "succeeded",
        actionId,
        entityType: tableId ? "table" : undefined,
        entityId: tableId,
        metadata: { operation: "close", table_id: tableId }
      });
    }
    setTableLabel("");
  }, [easyOrders, tableLabel]);

  const navigate = useCallback((next: Screen) => {
    const actionId = diagnostics.newActionId();
    // Leaving for another tab abandons a table move in progress; otherwise the grid would still
    // be in picker mode the next time it is opened.
    setChangeTable(null);
    leaveTable(actionId);
    if (next === "settings") {
      setSettingsHomeKey((value) => value + 1);
    }
    if (next !== screen) {
      diagnostics.record({
        name: "navigation.screen",
        category: "USER_ACTION",
        outcome: "succeeded",
        actionId,
        metadata: { operation: "open", screen: next, previous_screen: screen }
      });
    }
    setScreen(next);
  }, [leaveTable, screen]);

  const cancelChangeTable = useCallback(() => {
    const back = changeTable?.sourceLabel ?? "";
    setChangeTable(null);
    if (back) {
      openTable(back);
    }
  }, [changeTable, openTable]);

  const dismissNotificationPrompt = useCallback(() => {
    localStorage.setItem("easyorders.pwa.notificationsAsked", "1");
    setNotificationPrompt(false);
  }, []);

  // The device Back button: a table move is cancelled, an open table is left, any other tab
  // returns to the tables. Dialogs and sub-pages register their own, inner steps.
  useBackHandler(
    Boolean(easyOrders.session) && (screen !== "tables" || Boolean(tableLabel) || Boolean(changeTable)),
    () => {
      if (changeTable) {
        cancelChangeTable();
      } else if (tableLabel) {
        leaveTable();
      } else {
        navigate("tables");
      }
    },
    "screen"
  );
  useBackHandler(notificationPrompt, dismissNotificationPrompt);
  useBackHandler(
    Boolean(easyOrders.mfaChallenge) && !easyOrders.mfaRecoveryCodes.length,
    () => easyOrders.cancelMfa(),
    "page"
  );

  async function install() {
    if (installPrompt) {
      await installPrompt.prompt();
      await installPrompt.userChoice;
      setInstallPrompt(null);
      return;
    }
    if (isAppleMobile() && !isStandalone()) {
      easyOrders.setMessage(t("Στο Safari πατήστε Κοινή χρήση και μετά «Προσθήκη στην οθόνη Αφετηρίας»."));
    }
  }

  const updateDialog = updatePromptOpen ? (
    <div className="dialog-backdrop" role="presentation">
      <section
        className="permission-dialog update-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="update-title"
        aria-describedby="update-text"
      >
        <header>
          <RefreshCw />
          <div>
            <h2 id="update-title">{t("Η εφαρμογή ενημερώθηκε")}</h2>
            <p id="update-text">{t("Χρειάζεται επανεκκίνηση για να ανοίξει η νέα έκδοση. Ό,τι δεν έχει σταλεί δεν χάνεται.")}</p>
            <p className="update-dialog-hint">{t("Μπορείς να κάνεις επανεκκίνηση και αργότερα, από Ρυθμίσεις → Ενημέρωση εφαρμογής.")}</p>
          </div>
        </header>
        <footer>
          <button type="button" className="secondary-command" onClick={restartLater} disabled={restarting}>
            <Clock />
            <span>{t("Επανεκκίνηση αργότερα")}</span>
          </button>
          <button type="button" className="primary-command" onClick={restartNow} disabled={restarting}>
            <RefreshCw />
            <span>{restarting ? t("Επανεκκίνηση...") : t("Επανεκκίνηση τώρα")}</span>
          </button>
        </footer>
      </section>
    </div>
  ) : null;

  if (easyOrders.mfaChallenge || easyOrders.mfaRecoveryCodes.length) {
    return (
      <>
        <TwoFactorScreen
          challenge={easyOrders.mfaChallenge}
          recoveryCodes={easyOrders.mfaRecoveryCodes}
          busy={easyOrders.loginBusy}
          message={easyOrders.message}
          onComplete={easyOrders.completeMfa}
          onCancel={easyOrders.cancelMfa}
          onAcknowledgeRecoveryCodes={easyOrders.acknowledgeRecoveryCodes}
        />
        <Toast message={easyOrders.message} onClose={() => easyOrders.setMessage("")} />
        {updateDialog}
      </>
    );
  }

  if (!easyOrders.session) {
    return (
      <>
        <LoginScreen
          busy={easyOrders.loginBusy}
          message={easyOrders.message}
          onLogin={easyOrders.login}
        />
        <Toast message={easyOrders.message} onClose={() => easyOrders.setMessage("")} />
        {updateDialog}
      </>
    );
  }

  if (!easyOrders.setup) {
    return (
      <>
        <main className="loading-screen">
          <img src="/icons/icon-192.png" alt="" />
          <strong>EasyOrders</strong>
          <span>{easyOrders.connection === "offline" ? t("Αναμονή σύνδεσης...") : t("Συγχρονισμός...")}</span>
          {easyOrders.message ? <p className="loading-message" role="alert">{easyOrders.message}</p> : null}
          <button type="button" className="secondary-command" onClick={() => void easyOrders.sync(true, true)}>{t("Επανάληψη")}</button>
        </main>
        {updateDialog}
      </>
    );
  }

  const draft = tableLabel ? easyOrders.drafts[tableLabel] ?? [] : [];
  const tablePending = tableLabel
    ? pendingForTable(easyOrders.pending, easyOrders.orders, tableLabel)
    : undefined;
  // With products waiting to go out, the footer button is a send button — a queued or failed
  // print no longer blocks the next order. It stays a retry only while the previous submission
  // has yet to reach the server.
  const orderActionPending = tablePending && !(
    draft.length && isOrderRecorded(tablePending, easyOrders.orders)
  ) ? tablePending : undefined;

  return (
    <>
      <AppShell
        screen={screen}
        connection={easyOrders.connection}
        syncing={easyOrders.syncing}
        username={easyOrders.session.user.username}
        updateAvailable={updateAvailable}
        isAdmin={easyOrders.session.user.role === "admin"}
        tableDetailOpen={screen === "tables" && Boolean(tableLabel)}
        navHidden={Boolean(changeTable)}
        orderUsesInnerScroll={loadProductScrollMode() === "inner"}
        onNavigate={navigate}
        onSync={() => void easyOrders.sync(true, true)}
      >
        {screen === "tables" && tableLabel ? (
          <OrderScreen
            tableLabel={tableLabel}
            setup={easyOrders.setup}
            orders={easyOrders.orders}
            draft={draft}
            pending={orderActionPending}
            printers={easyOrders.enabledPrinters}
            busy={easyOrders.actionBusy}
            canEdit={
              easyOrders.session.user.role === "admin" ||
              easyOrders.session.user.can_edit === 1
            }
            canCancel={
              easyOrders.session.user.role === "admin" ||
              easyOrders.session.user.can_cancel === 1
            }
            onBack={() => leaveTable()}
            onDraftChange={(lines) => easyOrders.setDraft(tableLabel, lines)}
            onSubmit={(printerIds) => void easyOrders.submitOrder(tableLabel, draft, printerIds)}
            onCloseTable={async () => {
              // A closed table has nothing left to show, so go back to the grid — but only once
              // the server has confirmed it, otherwise the failure would scroll away unseen.
              if (await easyOrders.closeTable(tableLabel)) {
                setTableLabel("");
              }
            }}
            onSetServed={(lineIds, served) => void easyOrders.setServed(lineIds, served)}
            onCancelOrder={(orderId) => void easyOrders.cancelOrder(orderId)}
            onBeginChangeTable={(orderId) => {
              setChangeTable({ orderId, sourceLabel: tableLabel });
              setTableLabel("");
              setScreen("tables");
            }}
            onCorrectProduct={(input) => void easyOrders.correctProduct(input)}
          />
        ) : null}
        {screen === "tables" && !tableLabel ? (
          <TablesScreen
            setup={easyOrders.setup}
            orders={easyOrders.orders}
            pending={easyOrders.pending}
            isAdmin={easyOrders.session.user.role === "admin"}
            canMergeTables={
              easyOrders.session.user.role === "admin" ||
              easyOrders.session.user.can_merge_tables === 1
            }
            canChangeTableLayout={
              easyOrders.session.user.role === "admin" ||
              easyOrders.session.user.can_change_table_layout === 1
            }
            onOpenTable={openTable}
            onCreateTemporaryTable={easyOrders.createTemporaryTable}
            onSaveSetup={(next) => easyOrders.saveSetup(next)}
            onMoveTable={(tableId, row, col) => void easyOrders.moveTableLayout(tableId, row, col)}
            onMergeTables={(label, tableIds) => void easyOrders.mergeTables(label, tableIds)}
            onExtendMerge={(mergeId, label, tableIds) => void easyOrders.extendMerge(mergeId, label, tableIds)}
            onSplitTable={(mergeId) => void easyOrders.splitTable(mergeId)}
            changeTable={changeTable}
            onChangeTableTarget={async (label) => {
              if (!changeTable) {
                return;
              }
              const moved = changeTable.orderId === 0
                ? easyOrders.moveDraftToTable(changeTable.sourceLabel, label)
                : await easyOrders.changeOrderTable(
                  changeTable.orderId,
                  changeTable.sourceLabel,
                  label
                );
              if (moved) {
                // Android lands on the order at its new table; do the same rather than leaving
                // the waiter on the grid wondering whether it took.
                setChangeTable(null);
                openTable(label);
              }
            }}
            onCancelChangeTable={cancelChangeTable}
          />
        ) : null}
        {screen === "products" && easyOrders.session.user.role === "admin" ? (
          <ProductsScreen
            setup={easyOrders.setup}
            busy={easyOrders.actionBusy}
            onSaveSetup={(next) => easyOrders.saveSetup(next)}
          />
        ) : null}
        {screen === "history" ? (
          <HistoryScreen
            orders={easyOrders.orders}
            printers={easyOrders.enabledPrinters}
            canReprint={
              easyOrders.session.user.role === "admin" ||
              easyOrders.session.user.can_reprint === 1
            }
            canClear={easyOrders.session.user.role === "admin"}
            busy={easyOrders.actionBusy}
            onReprint={easyOrders.reprintOrder}
            onClear={easyOrders.clearHistory}
            onLoadReport={easyOrders.loadSalesPeriodReport}
            onPrintReport={easyOrders.printSalesPeriodReport}
          />
        ) : null}
        {screen === "settings" ? (
          <SettingsScreen
            key={settingsHomeKey}
            isAdmin={easyOrders.session.user.role === "admin"}
            currentUser={easyOrders.session.user}
            setup={easyOrders.setup}
            busy={easyOrders.actionBusy}
            onSaveSetup={(next) => easyOrders.saveSetup(next)}
            onLoadGatewayStatus={easyOrders.loadGatewayStatus}
            onChangePassword={easyOrders.changePassword}
            onEnableMfa={easyOrders.enableMfa}
            onDisableMfa={easyOrders.disableMfa}
            onTestPrint={(printerId) => void easyOrders.testPrint(printerId)}
            onProbePrinter={(printerId) => void easyOrders.probePrinter(printerId)}
            username={easyOrders.session.user.username}
            businessName={easyOrders.session.business?.name || easyOrders.session.businessSlug}
            connection={easyOrders.connection}
            lastSyncAt={easyOrders.lastSyncAt}
            deploymentMode={easyOrders.health?.deploymentMode || ""}
            theme={theme}
            themeColor={themeColor}
            onThemeColorChange={setThemeColor}
            updateAvailable={updateAvailable}
            checkingUpdate={checkingUpdate}
            installAvailable={!isStandalone() && (Boolean(installPrompt) || isAppleMobile())}
            onThemeChange={setTheme}
            onNotifications={() => easyOrders.requestNotifications()}
            onSync={() => void easyOrders.sync(true, true)}
            onInstall={() => void install()}
            onUpdate={restartNow}
            onCheckUpdate={() => void checkForPwaUpdate()}
            onLogout={() => void easyOrders.logout()}
          />
        ) : null}
      </AppShell>
      {notificationPrompt ? (
        <div className="dialog-backdrop" role="presentation">
          <section className="permission-dialog" role="dialog" aria-modal="true" aria-labelledby="notification-title">
            <header>
              <Bell />
              <div>
                <h2 id="notification-title">{t("Ειδοποιήσεις παραγγελιών")}</h2>
                <p>{t("Να ενεργοποιηθούν οι ειδοποιήσεις με ήχο;")}</p>
              </div>
            </header>
            <footer>
              <button
                type="button"
                className="secondary-command"
                onClick={dismissNotificationPrompt}
              >
                <X />
                <span>{t("Όχι τώρα")}</span>
              </button>
              <button
                type="button"
                className="primary-command"
                onClick={() => {
                  localStorage.setItem("easyorders.pwa.notificationsAsked", "1");
                  setNotificationPrompt(false);
                  void easyOrders.requestNotifications();
                }}
              >
                <Bell />
                <span>{t("Ενεργοποίηση")}</span>
              </button>
            </footer>
          </section>
        </div>
      ) : null}
      <Toast message={easyOrders.message} onClose={() => easyOrders.setMessage("")} />
      {updateDialog}
    </>
  );
}
